// Canonical-layer advisory guidance for value-creation levers that are NOT
// instrumented as priced (governed-dollar) levers. These are the industry
// playbook families a partner would still put on the table — network design,
// working capital, mode mix, etc. — but which Helix will not assign a dollar to
// until they are governed end to end. The advisory grade is deliberate: it
// carries DIRECTIONAL judgment (a materiality band, what data would instrument
// it, and where it sequences) with ZERO invented numbers, so leadership sees the
// full playbook without false precision.
//
// The bands and notes are declared here (governed, reviewable) rather than
// invented at narration time — the LLM narrates this guidance; it does not
// author it. A family with no entry here is simply named, not graded.

export type AdvisoryMateriality = 'high' | 'medium' | 'low';

// near_term: instrument from data the tenant likely already has.
// foundational: needs a data build (e.g. lane/network model) before sizing.
// parallel_cash: improves cash/returns, not operating income — runs on its own track.
// later: real but lower-priority for the current objective.
export type AdvisorySequencing = 'near_term' | 'foundational' | 'parallel_cash' | 'later';

export type AdvisoryLeverGuidance = Readonly<{
  materiality: AdvisoryMateriality;
  // What governed evidence would move this from advisory to a priced lever.
  dataNeeds: string;
  sequencing: AdvisorySequencing;
  // One line a partner would say about why it is on the table.
  rationale: string;
  // True when the lever improves cash/returns rather than operating income, so
  // it is never a candidate for the operating-income bridge even once governed.
  cashNotOperatingIncome?: boolean;
  // When the lever shares dollars with an already-priced family, name it so the
  // advisory readout cannot be read as additive to the bridge.
  overlapsPricedFamilyId?: string;
}>;

// Asset-light freight-brokerage calibration (the lighthouse account is a ~$17B
// 3PL). Bands reflect where the dollars realistically sit for an asset-light
// operator: purchased-transportation network economics dominate COGS, the owned
// asset base (capex/warehousing) is thin, and working capital is large because
// the company intermediates a huge gross freight flow.
const GUIDANCE: Record<string, AdvisoryLeverGuidance> = {
  network_optimization: {
    materiality: 'high',
    dataNeeds: 'lane, route, and facility model, shipment lane volumes, and service-level commitments',
    sequencing: 'foundational',
    rationale:
      'Network and routing design is the largest structural lever on purchased-transportation COGS, but it requires a governed lane model before any dollar is credible.',
  },
  mode_mix_shift: {
    materiality: 'medium',
    dataNeeds: 'shipment mode, weight and distance, service levels, and mode capacity',
    sequencing: 'near_term',
    rationale:
      'Shifting TL, LTL, intermodal, and parcel mix trims COGS where service tolerances allow; sizing needs shipment-level mode and capacity evidence.',
  },
  capacity_utilization: {
    materiality: 'medium',
    dataNeeds: 'empty miles, asset and trailer utilization, route plans, and load factors',
    sequencing: 'near_term',
    rationale:
      'Backhaul and load-factor gains reduce effective buy-rate, but the dollar depends on governed utilization and empty-mile evidence.',
  },
  fuel_efficiency: {
    materiality: 'medium',
    dataNeeds: 'fuel surcharges, accessorial charges, route distance, and carrier invoice lines',
    sequencing: 'near_term',
    rationale:
      'Fuel and accessorial leakage is recoverable at the invoice line; it overlaps the carrier program, so it must be de-conflicted before it is summed.',
    overlapsPricedFamilyId: 'carrier_renegotiation',
  },
  warehouse_productivity: {
    materiality: 'low',
    dataNeeds: 'warehouse labor hours, throughput, and facility cost by site',
    sequencing: 'later',
    rationale:
      'For an asset-light broker the owned warehousing footprint is thin (concentrated in managed and fresh operations), so the lever is real but smaller.',
  },
  working_capital: {
    materiality: 'high',
    dataNeeds: 'AR aging, DSO and DPO by customer and carrier, and payment-term terms',
    sequencing: 'parallel_cash',
    rationale:
      'Intermediating a very large gross freight flow makes DSO and DPO a large cash lever — but it improves cash and returns, not operating income.',
    cashNotOperatingIncome: true,
  },
  capex_discipline: {
    materiality: 'low',
    dataNeeds: 'capital plan, asset register, and project-level returns',
    sequencing: 'parallel_cash',
    rationale:
      'An asset-light model carries little capital, so capex discipline is a modest cash-and-returns lever rather than an operating-income driver.',
    cashNotOperatingIncome: true,
  },
  revenue_mix_shift: {
    materiality: 'medium',
    dataNeeds: 'customer and lane margin mix, and contribution by service line',
    sequencing: 'foundational',
    rationale:
      'Steering volume toward higher-spread customers and lanes lifts adjusted gross profit, but needs governed margin-mix evidence to size.',
  },
  growth_cross_sell: {
    materiality: 'medium',
    dataNeeds: 'customer product penetration, attach rates, and white-space mapping',
    sequencing: 'later',
    rationale:
      'Cross-sell into the existing book grows net revenue; it is a growth play and sits on a different track from the no-growth cost bridge.',
  },
  churn_reduction: {
    materiality: 'medium',
    dataNeeds: 'customer retention, churn cohorts, and revenue-at-risk',
    sequencing: 'near_term',
    rationale:
      'Protecting at-risk revenue defends adjusted gross profit; sizing needs governed retention and revenue-at-risk evidence.',
  },
  discount_leakage: {
    materiality: 'medium',
    dataNeeds: 'quote-to-invoice variance, discount authority, and realized-vs-quoted rate',
    sequencing: 'near_term',
    rationale:
      'Quote-to-invoice rate leakage recovers realized price; it shares the customer revenue line with the pricing lever, so it must be de-conflicted.',
    overlapsPricedFamilyId: 'pricing_uplift',
  },
  outside_contractors: {
    materiality: 'medium',
    dataNeeds: 'contingent-labor and outsourced-services spend, rates, and conversion candidates',
    sequencing: 'near_term',
    rationale:
      'Contingent and outsourced-services spend can be rationalized like the other SG&A categories once a governed spend cube is loaded.',
  },
  labor_arbitrage: {
    materiality: 'medium',
    dataNeeds: 'role and location cost, span and shoring mix, and transition feasibility',
    sequencing: 'foundational',
    rationale:
      'Location and shoring strategy on back-office roles is real but sensitive; it needs governed role-cost evidence and a transition view.',
  },
  segment_margin_cleanup: {
    materiality: 'medium',
    dataNeeds: 'segment P&L allocations and an exclusion receipt tying cleanup to named underlying actions',
    sequencing: 'foundational',
    rationale:
      'Segment cleanup is an allocation lens that overlaps every underlying lever; it can only be summed once exclusion from named actions is proven.',
    overlapsPricedFamilyId: 'segment_margin_cleanup',
  },
};

const FROZEN_GUIDANCE: Record<string, AdvisoryLeverGuidance> = Object.freeze(
  Object.fromEntries(
    Object.entries(GUIDANCE).map(([key, value]) => [key, Object.freeze(value)]),
  ),
);

export function getAdvisoryLeverGuidance(
  familyId: string,
): AdvisoryLeverGuidance | null {
  return FROZEN_GUIDANCE[familyId] ?? null;
}

export function listAdvisoryLeverGuidance(): ReadonlyArray<
  readonly [string, AdvisoryLeverGuidance]
> {
  return Object.freeze(
    Object.entries(FROZEN_GUIDANCE).map(
      ([key, value]) => Object.freeze([key, value] as const),
    ),
  );
}
